import './styles/main.css';

import { Router } from './lib/router.ts';
import { XaebiaScene } from './three/scene.ts';
import { XaebiaTerminal } from './components/Terminal.ts';

import { renderHome,     initHome     } from './pages/Home.ts';
import { renderAbout                  } from './pages/About.ts';
import { renderApps,     initApps     } from './pages/Apps.ts';
import { renderProjects, initProjects } from './pages/Projects.ts';
import { renderStore,    initStore    } from './pages/Store.ts';
import { renderBlog,     initBlog     } from './pages/Blog.ts';
import { renderContact,  initContact  } from './pages/Contact.ts';

// ── Loading progress ────────────────────────────────────────────────────────
const loadingBar    = document.getElementById('loading-bar') as HTMLElement;
const loadingStatus = document.getElementById('loading-status') as HTMLElement;
const loadingScreen = document.getElementById('loading-screen') as HTMLElement;

function setProgress(pct: number, status: string): void {
  if (loadingBar)    loadingBar.style.width = `${pct}%`;
  if (loadingStatus) loadingStatus.textContent = status;
}

// ── WebGL check ─────────────────────────────────────────────────────────────
function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch { return false; }
}

// ── Portal label overlay ─────────────────────────────────────────────────────
let portalLabelUpdateId = 0;
function startPortalLabels(scene: XaebiaScene): void {
  const container = document.getElementById('portal-labels')!;

  function updateLabels(): void {
    const positions = scene.getPortalScreenPositions(window.innerWidth, window.innerHeight);
    positions.forEach(p => {
      let el = container.querySelector(`[data-portal="${p.id}"]`) as HTMLElement | null;
      if (!el) {
        el = document.createElement('a');
        el.className = 'portal-label';
        el.setAttribute('data-portal', p.id);
        (el as HTMLAnchorElement).href = `#${p.id === 'projects' ? 'projects' : p.id}`;
        el.style.color = p.color;
        el.style.borderColor = `${p.color}30`;
        el.style.textShadow = `0 0 8px ${p.color}`;
        container.appendChild(el);
      }
      el.textContent = p.label;
      el.style.left  = `${p.x}px`;
      el.style.top   = `${p.y}px`;
      el.style.opacity = p.visible ? '1' : '0';
    });
    portalLabelUpdateId = requestAnimationFrame(updateLabels);
  }

  updateLabels();
}

function stopPortalLabels(): void {
  cancelAnimationFrame(portalLabelUpdateId);
  const container = document.getElementById('portal-labels');
  if (container) container.innerHTML = '';
}

// ── Page routing ──────────────────────────────────────────────────────────────
const router = new Router();
let sceneRef: XaebiaScene | null = null;
let labelsActive = false;

const PAGE_RENDERERS: Record<string, { render: () => string; init?: () => void }> = {
  home:     { render: renderHome,     init: initHome     },
  about:    { render: renderAbout                        },
  apps:     { render: renderApps,     init: initApps     },
  projects: { render: renderProjects, init: initProjects },
  store:    { render: renderStore,    init: initStore    },
  blog:     { render: renderBlog,     init: initBlog     },
  contact:  { render: renderContact,  init: initContact  },
};

function activatePage(name: string): void {
  const allPages = document.querySelectorAll('.page');
  const overlay  = document.getElementById('scene-overlay');

  allPages.forEach(el => el.classList.remove('active'));
  document.querySelectorAll('.nav-link').forEach(el => {
    el.classList.toggle('active', el.getAttribute('data-page') === name);
  });

  const pageEl = document.getElementById(`page-${name}`);
  if (!pageEl) return;

  const renderer = PAGE_RENDERERS[name] ?? PAGE_RENDERERS['home'];

  // Render once (idempotent check via data-rendered attribute)
  if (!pageEl.dataset.rendered) {
    pageEl.innerHTML = renderer.render();
    pageEl.dataset.rendered = '1';
    renderer.init?.();
  }

  pageEl.classList.add('active');

  // Dim scene on non-home pages; show labels only on home
  const isHome = name === 'home';
  overlay?.classList.toggle('dimmed', !isHome);

  if (isHome && sceneRef && !labelsActive) {
    startPortalLabels(sceneRef);
    labelsActive = true;
  } else if (!isHome && labelsActive) {
    stopPortalLabels();
    labelsActive = false;
  }

  if (isHome && sceneRef) {
    sceneRef.resetCamera();
  }
}

// ── Mode switching ────────────────────────────────────────────────────────────
function initModeSwitcher(): void {
  document.querySelectorAll('.mode-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.mode-tab').forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      const mode = (tab as HTMLElement).dataset.mode;
      document.body.dataset.mode = mode ?? 'browse';

      if (mode === 'play' && router.currentRoute() !== 'home') {
        router.navigate('home');
      }
      if (mode === 'build') {
        router.navigate('projects');
      }
    });
  });
}

// ── Mobile menu ───────────────────────────────────────────────────────────────
function initMobileMenu(): void {
  const btn   = document.getElementById('mobile-menu-btn');
  const links = document.getElementById('nav-links');
  if (!btn || !links) return;

  btn.addEventListener('click', () => {
    const open = links.classList.toggle('open');
    btn.setAttribute('aria-expanded', String(open));
  });

  links.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', () => {
      links.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
    });
  });
}

// ── Boot sequence ─────────────────────────────────────────────────────────────
async function boot(): Promise<void> {
  setProgress(10, 'Checking systems...');

  const webglOk = hasWebGL();

  setProgress(30, 'Loading world...');

  if (webglOk) {
    const canvas = document.getElementById('xaebia-canvas') as HTMLCanvasElement;
    try {
      sceneRef = new XaebiaScene(canvas);
      sceneRef.setNavigationHandler((page) => {
        activatePage(page);
      });
      setProgress(60, 'Building scene...');
    } catch (e) {
      console.warn('Three.js scene failed:', e);
    }
  } else {
    document.getElementById('xaebia-canvas')?.remove();
    const fallback = document.getElementById('webgl-fallback');
    fallback?.classList.remove('hidden');
  }

  setProgress(80, 'Initializing routes...');

  // Set up router
  router
    .on('home',     () => activatePage('home'))
    .on('about',    () => activatePage('about'))
    .on('apps',     () => activatePage('apps'))
    .on('projects', () => activatePage('projects'))
    .on('store',    () => activatePage('store'))
    .on('blog',     () => activatePage('blog'))
    .on('contact',  () => activatePage('contact'))
    .on('*',        () => activatePage('home'));

  initModeSwitcher();
  initMobileMenu();

  // Terminal
  const terminal = new XaebiaTerminal((page) => {
    router.navigate(page);
    terminal.close();
  });

  setProgress(95, 'Almost ready...');

  // Small delay so the loading bar reaches 100% visually
  await new Promise(r => setTimeout(r, 400));
  setProgress(100, 'Welcome to Xaebia.');

  await new Promise(r => setTimeout(r, 500));

  // Hide loading screen
  loadingScreen.classList.add('done');
  setTimeout(() => loadingScreen.remove(), 700);

  // Start router (triggers initial page render)
  router.init();

  // Start portal labels if on home
  if (sceneRef && router.currentRoute() === 'home') {
    startPortalLabels(sceneRef);
    labelsActive = true;
  }
}

boot().catch(console.error);
